import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  CalendarCheck, 
  Home, 
  GraduationCap, 
  Receipt, 
  History,
  LogOut,
  Menu,
  X,
  User,
  Ticket,
  CalendarRange,
  CalendarX,
  HelpCircle,
  ClipboardCheck,
  ArrowRightLeft,
  ShieldCheck,
  Flag,
  MessagesSquare,
  UsersRound
} from 'lucide-react';
import { Button } from './ui/button';
import { API } from '../utils/config';
import { useTour } from './tour/TourProvider';

// Voci di menu NON visibili ai tenant esterni Home Smart (funzioni interne HW)
const EXTERNAL_HIDDEN_PATHS = [
  '/responsabile/formazione',
  '/responsabile/eventi-formazione',
  '/responsabile/fatturazione',
  '/responsabile/ticket',
  '/responsabile/passaggi-consegne',
];

const ResponsableSidebar = ({ isOpen, setIsOpen, onLogout }) => {
  const location = useLocation();
  const { startTour } = useTour();
  const [badges, setBadges] = useState({
    ticket: 0,
    segnalazioni: 0,
    immobili: 0,
    arrivi: 0,
    formazione: 0,
    chiusure: 0
  });
  const [userName, setUserName] = useState('');
  const [isPropertyOwner, setIsPropertyOwner] = useState(false);
  const [isExternal, setIsExternal] = useState(() => {
    try {
      return !!JSON.parse(localStorage.getItem('hw_user') || '{}').is_external_tenant;
    } catch {
      return false;
    }
  });
  const token = localStorage.getItem('hw_token');

  // Fetch user profile to get name
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API}/api/user/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const name = data.name || `${data.first_name || ''} ${data.last_name || ''}`.trim();
          setUserName(name || 'Responsabile');
        }
      } catch (e) {
        console.error('Error fetching profile:', e);
      }
    };
    fetchUserProfile();

    // Rileva se l'utente è un tenant esterno (Home Smart / Mypasspartu)
    const fetchExternalFlag = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API}/api/auth/verify`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setIsExternal(!!data.user?.is_external_tenant);
        }
      } catch (e) {
        // ignora
      }
    };
    fetchExternalFlag();

    // Verifica se utente e' anche proprietario di almeno un immobile
    const fetchOwnerStatus = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API}/api/user/is-property-owner`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setIsPropertyOwner(!!data.is_owner);
        }
      } catch (e) {
        // Silently fail: pagina semplicemente nascosta
      }
    };
    fetchOwnerStatus();
  }, [token]);

  // Fetch badge counts
  useEffect(() => {
    fetchBadgeCounts();
    // Refresh every 30 seconds
    const interval = setInterval(fetchBadgeCounts, 30000);
    
    // Listen for ticketRead event to refresh badges immediately
    const handleTicketRead = () => {
      setTimeout(fetchBadgeCounts, 300);
    };
    window.addEventListener('ticketRead', handleTicketRead);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('ticketRead', handleTicketRead);
    };
  }, []);

  const fetchBadgeCounts = async () => {
    if (!token) return;
    
    try {
      // Fetch ticket unread count
      const ticketsRes = await fetch(`${API}/api/tickets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (ticketsRes.ok) {
        const tickets = await ticketsRes.json();
        const unreadTickets = tickets.reduce((acc, t) => acc + (t.unread_by_responsible || 0), 0);
        setBadges(prev => ({ ...prev, ticket: unreadTickets }));
      }

      const reportsRes = await fetch(`${API}/api/segnalazioni/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (reportsRes.ok) {
        const reportStats = await reportsRes.json();
        setBadges(prev => ({ ...prev, segnalazioni: reportStats.unread || 0 }));
      }

      // Fetch recently assigned properties (last 7 days, not viewed)
      const propsRes = await fetch(`${API}/api/responsible/my-properties`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (propsRes.ok) {
        const props = await propsRes.json();
        // Check localStorage for last viewed properties
        const lastViewed = localStorage.getItem('hw_last_viewed_properties');
        const lastViewedDate = lastViewed ? new Date(lastViewed) : new Date(0);
        const newProps = props.filter(p => {
          const assignedAt = p.assigned_at ? new Date(p.assigned_at) : null;
          return assignedAt && assignedAt > lastViewedDate;
        });
        setBadges(prev => ({ ...prev, immobili: newProps.length }));
      }

      // Fetch new bookings count (created in last 7 days, not viewed)
      const bookingsRes = await fetch(`${API}/api/responsible/my-bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (bookingsRes.ok) {
        const bookings = await bookingsRes.json();
        const lastViewedBookings = localStorage.getItem('hw_last_viewed_bookings');
        const lastViewedBookingsDate = lastViewedBookings ? new Date(lastViewedBookings) : new Date(0);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        const newBookings = bookings.filter(b => {
          const createdAt = b.created_at ? new Date(b.created_at) : 
                           b.imported_at ? new Date(b.imported_at) : null;
          return createdAt && createdAt > lastViewedBookingsDate && createdAt > sevenDaysAgo;
        });
        setBadges(prev => ({ ...prev, prenotazioni: newBookings.length }));
        
        // Count arrivals for next 3 days not viewed
        const lastViewedArrivi = localStorage.getItem('hw_last_viewed_arrivi');
        const lastViewedArriviDate = lastViewedArrivi ? new Date(lastViewedArrivi) : new Date(0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const threeDaysFromNow = new Date(today);
        threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
        
        const upcomingArrivals = bookings.filter(b => {
          const checkIn = b.check_in ? new Date(b.check_in) : null;
          const createdAt = b.created_at ? new Date(b.created_at) : 
                           b.imported_at ? new Date(b.imported_at) : null;
          // Show badge if: arrival in next 3 days AND (new booking OR not viewed arrivals page recently)
          return checkIn && checkIn >= today && checkIn <= threeDaysFromNow && 
                 createdAt && createdAt > lastViewedArriviDate;
        });
        setBadges(prev => ({ ...prev, arrivi: upcomingArrivals.length }));
      }

      // Fetch closures badge count (new closures not viewed)
      const closuresRes = await fetch(`${API}/api/responsible/my-closures`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (closuresRes.ok) {
        const closures = await closuresRes.json();
        const lastViewedChiusure = localStorage.getItem('hw_last_viewed_chiusure');
        const lastViewedChiusureDate = lastViewedChiusure ? new Date(lastViewedChiusure) : new Date(0);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        const newClosures = closures.filter(c => {
          const createdAt = c.created_at ? new Date(c.created_at) : 
                           c.imported_at ? new Date(c.imported_at) : null;
          return createdAt && createdAt > lastViewedChiusureDate && createdAt > sevenDaysAgo;
        });
        setBadges(prev => ({ ...prev, chiusure: newClosures.length }));
      }
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  };

  // Refresh badges when visiting specific pages (after API marks as read)
  useEffect(() => {
    // When visiting immobili page, mark as viewed
    if (location.pathname === '/responsabile/immobili') {
      localStorage.setItem('hw_last_viewed_properties', new Date().toISOString());
      setBadges(prev => ({ ...prev, immobili: 0 }));
    }
    
    // When visiting chiusure page, mark as viewed
    if (location.pathname === '/responsabile/chiusure') {
      localStorage.setItem('hw_last_viewed_chiusure', new Date().toISOString());
      setBadges(prev => ({ ...prev, chiusure: 0 }));
    }
    
    // When visiting ticket page or leaving it, refresh badge count
    // Small delay to allow API to mark messages as read
    if (location.pathname === '/responsabile/ticket' || location.pathname.startsWith('/responsabile/')) {
      const timer = setTimeout(() => {
        fetchBadgeCounts();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  const menuItems = [
    {
      path: '/responsabile/dashboard',
      icon: LayoutDashboard,
      label: 'Dashboard',
      enabled: true,
      badgeKey: null
    },
    {
      path: '/responsabile/arrivi-partenze',
      icon: CalendarCheck,
      label: 'Arrivi e Partenze',
      enabled: true,
      badgeKey: 'arrivi'
    },
    {
      path: '/responsabile/immobili',
      icon: Home,
      label: 'Immobili Gestiti',
      enabled: true,
      badgeKey: 'immobili'
    },
    {
      path: '/responsabile/chiusure',
      icon: CalendarX,
      label: 'Chiusure Proprietà',
      enabled: true,
      badgeKey: 'chiusure',
      testId: 'nav-chiusure'
    },
    {
      path: '/responsabile/formazione',
      icon: GraduationCap,
      label: 'Area Formazione',
      enabled: true,
      badgeKey: 'formazione'
    },
    {
      path: '/responsabile/eventi-formazione',
      icon: CalendarCheck,
      label: 'Eventi e Riunioni',
      enabled: true,
      badgeKey: null
    },
    {
      path: '/responsabile/fatturazione',
      icon: Receipt,
      label: 'Fatturazione',
      enabled: false,
      disabledNote: 'Verrà implementato - Per ora accedi al sito Holiday World',
      badgeKey: null
    },
    {
      path: '/responsabile/storico',
      icon: History,
      label: 'Storico Prenotazioni',
      enabled: true,
      badgeKey: 'prenotazioni'
    },
    {
      path: '/responsabile/calendario-sinottico',
      icon: CalendarRange,
      label: 'Calendario Sinottico',
      enabled: true,
      badgeKey: null
    },
    {
      path: '/responsabile/segnalazioni',
      icon: Flag,
      label: 'Segnalazioni',
      enabled: true,
      badgeKey: 'segnalazioni',
      testId: 'nav-segnalazioni'
    },
    {
      path: '/responsabile/ticket',
      icon: Ticket,
      label: 'Apri Ticket',
      enabled: true,
      badgeKey: 'ticket',
      testId: 'nav-ticket'
    },
    // NASCOSTA TEMPORANEAMENTE: scheda qualità da perfezionare prima del rilascio ai responsabili
    // {
    //   path: '/responsabile/schede-qualita',
    //   icon: ClipboardCheck,
    //   label: 'Schede Qualità',
    //   enabled: true,
    //   badgeKey: null,
    //   testId: 'nav-schede-qualita'
    // },
    {
      path: '/responsabile/passaggi-consegne',
      icon: ArrowRightLeft,
      label: 'Passaggi Consegne',
      enabled: true,
      badgeKey: null,
      testId: 'nav-passaggi-consegne'
    },
    {
      path: '/responsabile/ricevute-questura',
      icon: ShieldCheck,
      label: 'Ricevute Questura',
      enabled: true,
      badgeKey: null,
      testId: 'nav-ricevute-questura',
      ownerOnly: true  // Visibile solo ai responsabili che sono ANCHE proprietari
    },
    {
      path: '/responsabile/marketing',
      icon: MessagesSquare,
      label: 'Marketing',
      enabled: true,
      badgeKey: null,
      externalOnly: true,
      testId: 'nav-marketing'
    },
    {
      path: '/responsabile/collaboratori',
      icon: UsersRound,
      label: 'Collaboratori',
      enabled: true,
      badgeKey: null,
      externalOnly: true,
      testId: 'nav-collaboratori'
    },
    {
      path: '/responsabile/profilo',
      icon: User,
      label: 'Il Mio Profilo',
      enabled: true,
      badgeKey: null
    }
  ];


  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;
    
    if (!item.enabled) {
      return (
        <div className="relative group">
          <div className="flex items-center gap-3 px-4 py-3 text-slate-400 cursor-not-allowed rounded-lg">
            <div className="relative">
              <Icon className="h-5 w-5" />
            </div>
            <span className={`${!isOpen && 'hidden'} md:block`}>{item.label}</span>
          </div>
          {/* Tooltip for disabled item */}
          <div className={`${!isOpen && 'hidden'} hidden lg:block absolute left-full top-0 ml-2 px-3 py-2 bg-red-100 text-red-700 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 border border-red-200`}>
            {item.disabledNote}
          </div>
        </div>
      );
    }

    return (
      <NavLink
        to={item.path}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            isActive
              ? 'bg-blue-600 text-white'
              : 'text-slate-600 hover:bg-slate-100'
          }`
        }
        onClick={() => window.innerWidth < 768 && setIsOpen(false)}
        data-testid={item.testId || `nav-${item.path.split('/').pop()}`}
      >
        <Icon className="h-5 w-5" />
        <span className={`${!isOpen && 'hidden'} md:block flex-1`}>{item.label}</span>
        {/* Badge solo a destra del testo */}
        {badgeCount > 0 && (
          <span className="min-w-[22px] h-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1.5" data-testid={`${item.testId || item.path.split('/').pop()}-badge`}>
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile Toggle Button */}
      <Button
        variant="outline"
        size="sm"
        className="fixed top-3 left-3 z-50 md:hidden bg-white shadow-md border-slate-300 hover:bg-slate-50 px-3 py-1.5 rounded-lg"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="mobile-menu-toggle"
      >
        {isOpen ? <X className="h-5 w-5 mr-1" /> : <Menu className="h-5 w-5 mr-1" />}
        <span className="text-sm font-medium">{isOpen ? 'Chiudi' : 'Menu'}</span>
      </Button>

      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full bg-white border-r border-slate-200 z-40 transition-transform duration-300 ${
          isOpen ? 'translate-x-0 visible pointer-events-auto' : '-translate-x-full invisible pointer-events-none md:visible md:pointer-events-auto'
        } md:translate-x-0 md:static md:z-auto w-64`}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Impersonation Banner */}
          {localStorage.getItem('hw_impersonating') === 'true' && (
            <div className="bg-purple-600 text-white px-4 py-2 text-center">
              <p className="text-xs font-medium">Stai impersonando:</p>
              <p className="text-sm font-bold truncate">{localStorage.getItem('hw_impersonated_user')}</p>
              <button
                onClick={() => {
                  const adminToken = localStorage.getItem('hw_admin_token');
                  if (adminToken) {
                    localStorage.setItem('hw_token', adminToken);
                    localStorage.removeItem('hw_admin_token');
                    localStorage.removeItem('hw_impersonating');
                    localStorage.removeItem('hw_impersonated_user');
                    window.location.href = '/property-managers';
                  }
                }}
                className="mt-1 text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full"
              >
                Torna Admin
              </button>
            </div>
          )}
          
          {/* Logo/Header */}
          <div className="p-4 border-b">
            <div className="flex items-center gap-3">
              <img 
                src={isExternal ? "/home-smart-logo.png" : "/1.png"} 
                alt={isExternal ? "Home Smart" : "HWsmart Logo"} 
                className="w-10 h-10 object-contain"
                data-testid="sidebar-logo"
              />
              <div className={`${!isOpen && 'hidden'} md:block`}>
                <h1 className="font-bold text-slate-800 truncate max-w-[160px]">{userName || (isExternal ? 'Home Smart' : 'Holiday World')}</h1>
                <p className="text-xs text-slate-500">{isExternal ? 'Area Riservata Home Smart' : 'Portale Responsabile'}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} data-testid="sidebar-menu">
            {menuItems
              .filter(item => !item.ownerOnly || isPropertyOwner || isExternal)
              .filter(item => !item.externalOnly || isExternal)
              .filter(item => !(isExternal && EXTERNAL_HIDDEN_PATHS.includes(item.path)))
              .map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t space-y-2">
            {/* Pulsante Guida/Tour (solo interni HW) */}
            {!isExternal && (
            <Button
              variant="ghost"
              className="w-full justify-start text-blue-600 hover:bg-blue-50 hover:text-blue-700"
              onClick={() => startTour('first_time')}
              data-testid="btn-guide-tour"
            >
              <HelpCircle className="h-5 w-5 mr-3" />
              <span className={`${!isOpen && 'hidden'} md:block`}>Guida</span>
            </Button>
            )}
            
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={onLogout}
              data-testid="responsible-logout-button"
            >
              <LogOut className="h-5 w-5 mr-3" />
              <span className={`${!isOpen && 'hidden'} md:block`}>Esci</span>
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default ResponsableSidebar;
